'use client'

import { IN_VIEW_ROOT_MARGIN_PX } from '@/hooks/use-in-view'
import { getScrollParent } from '@/lib/scroll-parent'

/**
 * Page-level memory budget for attachment previews (renderer OOM structural
 * fix, P1 PR-6).
 *
 * Two independent accounts live here:
 *
 * 1. **Preview source bytes** — the text that `useFilePreviewText` streams into
 *    the SWR cache (markdown / code / json at 2 MiB, HTML artifacts at 25 MiB).
 *    SWR's default cache is an unbounded `Map` with no LRU order and no byte
 *    account, so a long chat could accumulate hundreds of megabytes of source
 *    strings that nothing ever releases. Entries carry their exact streamed
 *    byte count, a refCount, and their own pre-bound eviction closure. When the
 *    total exceeds {@link PREVIEW_SOURCE_BUDGET_BYTES}, unpinned entries are
 *    evicted in LRU order.
 *
 * 2. **Heavy viewer mounts** — pdf / spreadsheet / docx / HTML viewers each
 *    hold a parsed document (pdf.js page canvases, a SheetJS workbook, a
 *    mammoth-converted `srcDoc` iframe). Their footprint lives in Blink /
 *    PartitionAlloc, not the JS heap, and is not observable from script, so it
 *    is capped by *count* ({@link MAX_AUTO_MOUNTED_HEAVY_VIEWERS}) rather than
 *    by bytes. Over the cap, thumbnails degrade to a static tile.
 *
 *    Note what this account does NOT have: there is no LRU for heavy viewers.
 *    The count cap plus the visibility ranking in
 *    {@link admitHeavyViewerConsumers} is the entire mechanism, so nothing
 *    downstream will notice and correct a ranking that has gone stale. That is
 *    why the visibility signal must stay live rather than latch.
 *
 * Guarantees:
 * - **Only bytes SWR actually holds are accounted.** Measuring and accounting
 *   are two separate steps on purpose: the fetcher only MEASURES
 *   ({@link measurePreviewSourceBytes}), and the bytes are not charged to the
 *   budget until a mounted consumer has observed SWR's committed `data`
 *   ({@link observePreviewSource}). Charging inside the fetcher would be wrong
 *   — SWR discards a response whose revalidation was overtaken by a `mutate`,
 *   so an eviction racing an in-flight fetch would leave the registry
 *   over-accounting and evicting genuinely-cached live entries.
 *   The converse does NOT hold, deliberately: bytes that SWR commits after
 *   every consumer has unmounted stay resident and UNCHARGED until some
 *   consumer mounts on the key again (at which point the surviving
 *   measurement charges them — see `measurements`). That direction only
 *   loosens the budget; it can never blank a live tile, which is why it is
 *   the acceptable side to err on.
 * - **A *continuously* pinned entry is never evicted by the budget.**
 *   `refCount > 0` means a mounted consumer is displaying it; `mutate(key,
 *   undefined)` would blank a visible tile. Staying over budget is the lesser
 *   failure. Note the word: the sweep runs on release, so an entry whose
 *   refCount momentarily touches 0 between a release and a re-retain is
 *   eligible in that instant. Nothing does that today, and what keeps it that
 *   way is that the pinning effect in `useFilePreviewText` depends on
 *   `[swrKey]` alone — a dependency on any re-created value would tear the pin
 *   down mid-life. That dep array is itself locked by a test
 *   (`file-preview-evict.test.ts`), which is the enforcement point; the
 *   alternative, deferring the sweep to a microtask, would make eviction
 *   timing unobservable to callers and much harder to test for no gain today.
 * - **Exactly one evictor.** This module is the sole owner of preview-source
 *   eviction. Unmount-time eviction (`HtmlBody`) declares intent through
 *   {@link requestPreviewSourceEviction} instead of calling `mutate` itself, so
 *   the byte account and the SWR cache can never disagree — a bypassing
 *   `mutate` would leave this registry believing the bytes are still resident.
 * - **refCount symmetry under StrictMode.** `retain`/`release` are exact
 *   inverses and depend only on the cache key, so React's double-invoked mount
 *   effects settle at the same refCount as a single invocation.
 * - `subscribe` + the per-consumer snapshot form a `useSyncExternalStore`
 *   pair; the module-level registry shape follows
 *   `lib/attachment-upload-cache.ts`.
 *
 * `performance.memory` is telemetry ONLY (design UQ-8): it is Chromium-only,
 * quantized, and reports the JS heap — while the incident's 11.57 GiB lived in
 * PartitionAlloc, outside it. Nothing in this module branches on it.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
} from 'react'

/**
 * Total budget for preview source strings held in the SWR cache (design UQ-5).
 * Initial value; recalibrate from telemetry rather than by guesswork.
 */
// NOT exported, deliberately. While it was, the single-comparison rule this
// module depends on was enforceable only inside this file: a consumer could
// import the constant and short-circuit the render gate with its own
// comparison, and every test stayed green. Un-exporting makes that
// unwriteable rather than merely discouraged. Tests read it through
// `previewSourceBudgetBytesForTest()`, which is greppable by name.
const PREVIEW_SOURCE_BUDGET_BYTES = 64 * 1024 * 1024

/**
 * How many heavy viewers may be mounted *automatically* (i.e. thumbnails) at
 * once (design UQ-5). User-initiated full-mode viewers are always admitted and
 * consume capacity, so an open preview dialog costs a thumbnail its slot.
 */
export const MAX_AUTO_MOUNTED_HEAVY_VIEWERS = 4

interface PreviewSourceEntry {
  /**
   * Exact streamed byte count, or 0 while the entry is only a mount
   * placeholder (fetch in flight, or SWR has nothing committed).
   * Deliberately not `String#length` — that counts UTF-16 code units.
   */
  bytes: number
  /** Mounted consumers. `> 0` blocks budget eviction. */
  refCount: number
  /** LRU ordering key. */
  lastAccessSeq: number
  /**
   * Pre-bound single-key SWR eviction for this entry (§2.5 carve-out). A
   * placeholder no-op until bytes are accounted; only entries with bytes are
   * ever LRU-evicted, so the no-op is never the one that runs.
   */
  evict: () => void
  /** Set when eviction was requested while the entry was still pinned. */
  evictOnLastRelease: boolean
  /**
   * Capacity held for an in-flight fetch that has not been charged yet.
   *
   * Without this the budget is blind exactly when it matters most. Admission
   * is evaluated during render, so on first paint every thumbnail on the page
   * evaluates it before any of them has recorded a single byte: 100 of 100
   * get admitted and 200 MiB lands. A reservation taken when the fetch STARTS
   * is what serializes those decisions — the fetcher's synchronous prologue
   * runs to completion before the next one begins, so reservations accumulate
   * and the burst is bounded. Replaced by real bytes on `observePreviewSource`.
   */
  reserved: number
}

const sources = new Map<string, PreviewSourceEntry>()

/**
 * Exact wire size per cache key, measured by the fetcher and held until the
 * key's SWR entry is evicted. Separate from `sources` because measuring and
 * charging happen at different moments (see the module guarantees), and
 * because a consumer that mounts onto an already-cached key never re-runs the
 * fetcher and would otherwise have no size to charge.
 *
 * Deletion path (the frontend architecture baseline requires one for any
 * accumulating map) is
 * TWO-part, because eviction alone does not cover every key: a fetch that
 * lands after its consumer unmounted (or one SWR discards) leaves a
 * measurement with no entry, and nothing will ever evict an entry that does
 * not exist.
 *   1. `evictEntry` drops the measurement alongside the entry it belongs to.
 *   2. {@link MAX_TRACKED_MEASUREMENTS} caps the map, trimming least-recently
 *      measured keys first, which is what bounds the orphans from (1)'s gap.
 * Losing a measurement is harmless: an already-charged entry keeps its
 * `bytes`, and an uncharged key is simply re-measured on its next fetch.
 */
const measurements = new Map<string, number>()

/**
 * Hard cap on the measurement map. Sized well above the number of distinct
 * previews one session realistically touches, so the trim is a backstop
 * against unbounded growth rather than something the happy path hits.
 */
const MAX_TRACKED_MEASUREMENTS = 256

// Two independent channels. They were one, which meant every heavy-viewer slot
// transition re-rendered every text preview on the page (and vice versa) even
// though neither account can affect the other's consumers.
const heavyViewerListeners = new Set<() => void>()
const sourceBudgetListeners = new Set<() => void>()

let snapshotVersion = 0
let sourceBudgetVersion = 0

// Source accounting deliberately does not notify on every change (that would
// re-render every mounted viewer on ordinary scroll churn). It notifies when
// capacity frees AND somebody was refused while it was scarce.
let refusalOutstanding = false
let lastAdmissionLoad = 0

// LRU order uses a monotonic sequence, not `Date.now()`: two accesses inside
// the same millisecond tie under a clock, which makes eviction order
// non-deterministic (and unit tests dependent on fake timers).
let accessSeq = 0

function nextAccessSeq(): number {
  accessSeq += 1
  return accessSeq
}

/**
 * Notify `useSyncExternalStore` subscribers.
 *
 * Only heavy-viewer transitions notify. Source-byte accounting deliberately
 * does not: no React subtree reads it (consumers re-render from the SWR cache
 * itself), and notifying on every fetch/mount/unmount would re-render every
 * mounted viewer on ordinary scroll churn.
 */
function notify(): void {
  snapshotVersion += 1
  for (const listener of heavyViewerListeners) {
    listener()
  }
}

/** Wake source-budget subscribers — only ever on an over/under-budget edge. */
function notifySourceBudget(): void {
  sourceBudgetVersion += 1
  for (const listener of sourceBudgetListeners) {
    listener()
  }
}

/**
 * Wake consumers when capacity frees, if any of them were actually refused.
 *
 * Driven by refusals rather than by a threshold, because no threshold can
 * stand in for admission: admission is per-cap (`load + cap <= BUDGET`), so a
 * 2 MiB thumbnail and a 25 MiB document are refused at different loads. Any
 * single boolean picked to approximate that is wrong for one of them — which
 * is exactly how the previous two versions failed. Recording that *someone*
 * was refused, and notifying the next time the load drops, is correct for
 * every cap without needing to know any of them.
 *
 * Precise in both directions: no notify when nobody is waiting, and at most
 * one per refusal episode, so it cannot storm.
 */
function syncBudgetSignal(): void {
  const load = admissionLoadBytes()
  const freed = load < lastAdmissionLoad
  lastAdmissionLoad = load
  if (!freed || !refusalOutstanding) return
  refusalOutstanding = false
  notifySourceBudget()
}

/**
 * Record that a consumer was turned away and is waiting for capacity.
 *
 * NOTE: reached from `canAdmitPreviewSource`, which consumers call during
 * React's render phase — so this mutates module state during render, the very
 * thing `useSyncExternalStore` exists to keep out of render. It is safe here
 * (the only effect is arming a boolean; a render React discards merely arms it
 * spuriously, costing at most one extra notify) and it is also LOad-BEARING:
 * re-arming correctness depends on it running on EVERY render. Two consumers
 * refused at different caps both re-arm because each re-runs the gate each
 * render, so whichever one loses the race for freed capacity immediately
 * re-arms itself and is woken by the next release.
 *
 * Moving this into an effect would look like tidying and would silently break
 * that: a consumer whose effect does not re-run would never re-arm, and would
 * sit on its placeholder for the life of the route. If you are here to clean
 * this up, that is what you would be removing.
 */
function noteRefusal(): void {
  refusalOutstanding = true
}

function totalSourceBytes(): number {
  let total = 0
  for (const entry of sources.values()) {
    total += entry.bytes
  }
  return total
}

/**
 * Bytes the budget must consider committed: charged bytes, plus the capacity
 * reserved by fetches that are in flight. Admission uses this rather than
 * {@link totalSourceBytes} so a burst of simultaneous starts cannot each see
 * an empty budget. Telemetry keeps reporting only what is actually resident.
 */
function admissionLoadBytes(): number {
  let total = 0
  for (const entry of sources.values()) {
    // Additive, not `bytes || reserved`: a charged entry must have had its
    // reservation cleared by `observePreviewSource`, and summing both is what
    // makes a failure to clear it show up as double-counting instead of being
    // silently masked.
    total += entry.bytes + entry.reserved
  }
  return total
}

/**
 * Drop an entry, its measurement, and the SWR data behind it.
 *
 * `evict` defaults to the entry's own pre-bound closure; an explicit one is
 * passed when the caller (the unmount-time request) supplies a fresher binding.
 */
function evictEntry(
  key: string,
  entry: PreviewSourceEntry,
  evict: () => void = entry.evict,
): void {
  sources.delete(key)
  measurements.delete(key)
  evict()
}

/**
 * Evict unpinned entries in LRU order until the total fits the budget.
 *
 * Pinned entries are skipped entirely — if everything resident is pinned the
 * registry stays over budget on purpose (see the module guarantees).
 */
function enforceBudget(): void {
  let total = totalSourceBytes()
  if (residentWithinBudget(total)) {
    // Still sync: the caller may have just replaced a pessimistic reservation
    // with a much smaller real size, which drops the admission load below the
    // line even though no eviction was needed. Returning without reporting
    // that is how refused previews got stranded.
    syncBudgetSignal()
    return
  }

  const candidates = [...sources.entries()]
    .filter(([, entry]) => entry.refCount === 0 && entry.bytes > 0)
    .sort((a, b) => a[1].lastAccessSeq - b[1].lastAccessSeq)

  for (const [key, entry] of candidates) {
    if (residentWithinBudget(total)) break
    total -= entry.bytes
    evictEntry(key, entry)
    reportEviction(key, entry.bytes)
  }
  syncBudgetSignal()
}

/**
 * Evict unpinned entries, least recently used first, until `cap` more bytes
 * would fit inside the budget (or nothing evictable is left).
 */
function reclaimUnpinnedFor(cap: number): void {
  const candidates = [...sources.entries()]
    .filter(([, entry]) => entry.refCount === 0 && entry.bytes > 0)
    .sort((a, b) => a[1].lastAccessSeq - b[1].lastAccessSeq)

  for (const [key, entry] of candidates) {
    if (fitsInBudget(cap)) break
    evictEntry(key, entry)
    reportEviction(key, entry.bytes)
  }
  syncBudgetSignal()
}

function noopEvict(): void {}

/**
 * Pin a preview source entry for a mounted consumer.
 *
 * Takes no eviction closure: an entry cannot be evicted before it has bytes,
 * and bytes only arrive through {@link observePreviewSource}, which supplies
 * the real binding. Keeping the closure out of this call is what lets the
 * caller's effect depend on the cache key alone — a dependency on a
 * re-created closure would tear the pin down and rebuild it mid-life, and the
 * transient `refCount === 0` in between could evict a visible entry.
 */
export function retainPreviewSource(key: string): void {
  const entry = sources.get(key)
  if (entry) {
    entry.refCount += 1
    entry.lastAccessSeq = nextAccessSeq()
    return
  }
  sources.set(key, {
    bytes: 0,
    refCount: 1,
    lastAccessSeq: nextAccessSeq(),
    evict: noopEvict,
    evictOnLastRelease: false,
    reserved: 0,
  })
}

/**
 * Unpin a preview source entry. Does NOT evict by itself — that is the whole
 * point of the refCount model (design §3-C: "unmount only unpins"), because a
 * virtualized list scrolls consumers in and out constantly and evict-on-unmount
 * would thrash the fetches.
 *
 * Two exceptions run eviction here: a deferred unmount-time request
 * (`evictOnLastRelease`), and a re-check of the budget now that one more entry
 * became evictable.
 */
export function releasePreviewSource(key: string): void {
  const entry = sources.get(key)
  if (!entry) return
  entry.refCount = Math.max(0, entry.refCount - 1)
  if (entry.refCount > 0) return

  if (entry.evictOnLastRelease) {
    evictEntry(key, entry)
    // This can take the total from over to under; without it the edge goes
    // unreported and nothing wakes the consumers waiting on capacity.
    syncBudgetSignal()
    return
  }
  if (entry.bytes === 0) {
    // Nothing accounted (fetch in flight, or it failed). Drop the placeholder
    // and with it any reservation, so an abandoned fetch cannot hold capacity
    // forever; `observePreviewSource` recreates the entry if a consumer ever
    // does see the data.
    sources.delete(key)
    syncBudgetSignal()
    return
  }
  enforceBudget()
  syncBudgetSignal()
}

/**
 * Record the exact number of bytes a preview source cost on the wire.
 *
 * Called from the SWR fetcher. This does NOT charge the budget: at this point
 * SWR has not yet decided whether to keep the response (a `mutate` that
 * overtook the revalidation makes it discard the body), so charging here would
 * over-account. {@link observePreviewSource} does the charging once a consumer
 * can see the committed data.
 */
export function measurePreviewSourceBytes(key: string, bytes: number): void {
  // Delete before set so re-measuring moves the key to the back of the Map's
  // insertion order — that makes the trim below least-recently-measured-first.
  measurements.delete(key)
  measurements.set(key, bytes)
  while (measurements.size > MAX_TRACKED_MEASUREMENTS) {
    const oldest = measurements.keys().next().value
    if (oldest === undefined) break
    measurements.delete(oldest)
  }
}

/**
 * Charge a preview source entry with its measured bytes and enforce the budget.
 *
 * Called from the consumer once SWR reports committed `data` for the key —
 * which is the proof that the string is really resident. Safe to call on every
 * render pass: it is idempotent for an unchanged measurement.
 *
 * A consumer that mounts onto an already-cached key never re-runs the fetcher,
 * which is why the measurement outlives it.
 */
export function observePreviewSource(key: string, evict: () => void): void {
  const bytes = measurements.get(key)
  if (bytes === undefined) return

  const entry = sources.get(key)
  if (entry) {
    const unchanged = entry.bytes === bytes
    entry.bytes = bytes
    entry.reserved = 0
    entry.evict = evict
    if (unchanged) {
      // A repeat observe still clears any lingering reservation above, so the
      // load can have moved even when the byte count did not.
      syncBudgetSignal()
      return
    }
    entry.lastAccessSeq = nextAccessSeq()
    if (entry.refCount === 0 && entry.evictOnLastRelease) {
      evictEntry(key, entry)
      syncBudgetSignal()
      return
    }
  } else {
    sources.set(key, {
      bytes,
      refCount: 0,
      lastAccessSeq: nextAccessSeq(),
      evict,
      evictOnLastRelease: false,
      reserved: 0,
    })
  }
  enforceBudget()
}

/**
 * Request immediate eviction of one preview source (the unmount-time path P0
 * introduced for the ≤25 MiB HTML string).
 *
 * The registry executes it, so the byte account and the SWR cache move
 * together. In practice the requester's own consumer has already unpinned by
 * the time this runs (the hook's effect is registered first, so its cleanup
 * runs first), which means the eviction is immediate. The pinned branch is
 * defensive: should a second consumer ever hold the same key, the eviction is
 * deferred to the last release rather than blanking a visible tile — it is
 * never dropped.
 */
export function requestPreviewSourceEviction(
  key: string,
  evict: () => void,
): void {
  const entry = sources.get(key)
  if (!entry) {
    // Never accounted, or already evicted. Honour the intent anyway — the
    // caller may still have an SWR entry (or an in-flight fetch) to clear.
    measurements.delete(key)
    evict()
    return
  }
  if (entry.refCount > 0) {
    entry.evictOnLastRelease = true
    // The requester's binding becomes the entry's evictor, so the deferred run
    // clears the same key through the same builder.
    entry.evict = evict
    return
  }
  evictEntry(key, entry, evict)
  syncBudgetSignal()
}

/**
 * May a preview source fetch start?
 *
 * This is the budget's own enforcement, and it exists because eviction alone
 * cannot enforce anything: a mounted consumer pins its entry, the sweep skips
 * pinned entries, and today's chat timeline mounts every message — so a long
 * chat full of text artifacts pins its way straight past the budget with
 * nothing evictable. Refusing to START new fetches bounds the total without
 * ever blanking something already on screen, which eviction of pinned entries
 * would do. (Two sibling PRs will shrink the mounted set, but a budget whose
 * enforcement depends on other branches is not a budget.)
 *
 * `required` is the user-initiated escape hatch — opening a preview dialog must
 * never be refused. Entries already holding bytes are always re-admitted, so a
 * tile that is loaded and displayed can never flip back to deferred.
 */
export function canAdmitPreviewSource(
  key: string,
  cap: number,
  required: boolean,
): boolean {
  if (required) return true
  const entry = sources.get(key)
  if (entry && (entry.bytes > 0 || entry.reserved > 0)) return true
  // `fitsInBudget(cap)` — the SAME call the fetcher makes. It used to ask the
  // weaker question "is there any room at all", which said yes in the band
  // `BUDGET - cap < load < BUDGET` while the fetcher said no. The key then
  // never nulled, so the cached `deferred` error was permanent: nothing
  // re-runs a fetcher on a non-null key (`revalidateOnFocus` is false and
  // `onErrorRetry` only retries `network`). That band is where a saturating
  // page settles, so it was the steady state, not an edge.
  if (fitsInBudget(cap)) return true
  noteRefusal()
  return false
}

/**
 * Atomically claim capacity for a fetch that is about to start.
 *
 * This, not {@link canAdmitPreviewSource}, is the authoritative gate. The
 * render-phase check is only a cheap filter: React renders every thumbnail
 * before running any effect, so during first paint they all see the same empty
 * budget. This runs in the SWR fetcher instead — after render, once per key,
 * and its synchronous prologue completes before the next fetcher's begins — so
 * concurrent starts observe each other's reservations and the burst is bounded
 * at the budget instead of at the number of tiles on the page.
 *
 * `cap` is the fetch's own byte ceiling, so a reservation can never understate
 * what the response might cost.
 */
/**
 * THE ONLY PLACE `PREVIEW_SOURCE_BUDGET_BYTES` IS COMPARED AGAINST ANYTHING.
 *
 * Every question about the budget is a call to this or to one of the named
 * variants below — never a fresh inline comparison. That rule exists because
 * this module shipped the same bug three times: two predicates that should
 * have been measuring one quantity measured two. The wake-up flag tested
 * charged bytes while admission tested charged + reserved; then the
 * render-phase gate omitted the `+ cap` the fetcher gate applied, opening a
 * band where render said yes and the fetcher said no. Both were invisible
 * because each comparison read correctly on its own line.
 *
 * If a caller needs a different question, add a named variant here. Do not
 * write `<`, `<=`, `>` or `>=` against the constant anywhere else — a test
 * enforces it.
 */
function withinBudget(load: number, additional: number): boolean {
  return load + additional <= PREVIEW_SOURCE_BUDGET_BYTES
}

/** Would `cap` more bytes still fit alongside everything already claimed? */
function fitsInBudget(cap: number): boolean {
  return withinBudget(admissionLoadBytes(), cap)
}

/**
 * Is what is actually RESIDENT within the budget?
 *
 * Deliberately a different operand from {@link fitsInBudget}: eviction decides
 * what to free, so it must weigh bytes that exist, not capacity promised to
 * fetches still in flight — reserving would otherwise evict live entries to
 * make room for something that has not arrived. That is a genuinely different
 * question, not a second implementation of the same one, and it is named so
 * the next reader does not have to re-derive the difference from an inline
 * operand. Having to re-derive it is how the first of this module's three
 * mismatched-predicate bugs happened.
 */
function residentWithinBudget(residentBytes: number): boolean {
  return withinBudget(residentBytes, 0)
}

export function reservePreviewSourceFetch(
  key: string,
  cap: number,
  required: boolean,
): boolean {
  const entry = sources.get(key)
  if (entry && entry.bytes > 0) return true
  if (!required && !fitsInBudget(cap)) {
    noteRefusal()
    // Reclaim on demand before refusing. The background sweep only runs when
    // the total is strictly OVER budget and stops as soon as it is back at or
    // under it, while admission needs strictly UNDER — so at exactly the
    // budget, with any amount of evictable material sitting there, nothing
    // would ever break the tie and every new preview would be refused
    // permanently. Asking for room is also simply more correct than refusing
    // while reclaimable bytes exist.
    reclaimUnpinnedFor(cap)
    if (!fitsInBudget(cap)) return false
    // Reclaim found room after all, so nobody is left waiting on this one.
  }
  if (entry) {
    entry.reserved = cap
  } else {
    sources.set(key, {
      bytes: 0,
      refCount: 0,
      lastAccessSeq: nextAccessSeq(),
      evict: noopEvict,
      evictOnLastRelease: false,
      reserved: cap,
    })
  }
  // Taking a reservation is one of the three ways the admission load moves.
  syncBudgetSignal()
  return true
}

/** Drop a reservation whose fetch failed. Success hands off to `observe`. */
export function releasePreviewSourceReservation(key: string): void {
  const entry = sources.get(key)
  if (!entry || entry.reserved === 0) return
  entry.reserved = 0
  if (entry.bytes === 0 && entry.refCount === 0) sources.delete(key)
  syncBudgetSignal()
}

export interface PreviewMemoryTelemetry {
  /** Preview source bytes currently accounted by the registry. */
  sourceBytes: number
  /** Capacity held by in-flight fetches that have not been charged yet. */
  reservedBytes: number
  /** Accounted entries (including pinned ones and zero-byte placeholders). */
  entryCount: number
  /** Entries currently pinned by a mounted consumer. */
  pinnedEntryCount: number
  /** Heavy viewers currently holding a slot (thumbnails + full-mode viewers). */
  heavyViewerCount: number
  /** Heavy viewers mounted but degraded to a static tile by the cap. */
  deniedHeavyViewerCount: number
  /**
   * `performance.memory.usedJSHeapSize` when the browser exposes it (Chromium
   * only), else `null`. TELEMETRY ONLY — never a trigger (design UQ-8): it is
   * quantized, Chromium-specific, and covers only the JS heap, while the
   * incident's footprint lived in Blink / PartitionAlloc outside it.
   */
  jsHeapUsedBytes: number | null
}

interface ChromiumPerformanceMemory {
  usedJSHeapSize?: number
}

function readJsHeapUsedBytes(): number | null {
  if (typeof performance === 'undefined') return null
  const memory = (performance as { memory?: ChromiumPerformanceMemory }).memory
  const used = memory?.usedJSHeapSize
  return typeof used === 'number' ? used : null
}

/**
 * Current registry state, for diagnostics on demand and for the dev-console
 * eviction log below. Never a trigger.
 */
export function getPreviewMemoryTelemetry(): PreviewMemoryTelemetry {
  let pinnedEntryCount = 0
  for (const entry of sources.values()) {
    if (entry.refCount > 0) pinnedEntryCount += 1
  }
  let heavyViewerCount = 0
  let deniedHeavyViewerCount = 0
  for (const consumer of heavyViewerConsumers) {
    if (consumer.state === 'granted') heavyViewerCount += 1
    else if (consumer.state === 'denied') deniedHeavyViewerCount += 1
  }
  let reservedBytes = 0
  for (const entry of sources.values()) {
    reservedBytes += entry.reserved
  }
  return {
    sourceBytes: totalSourceBytes(),
    reservedBytes,
    entryCount: sources.size,
    pinnedEntryCount,
    heavyViewerCount,
    deniedHeavyViewerCount,
    jsHeapUsedBytes: readJsHeapUsedBytes(),
  }
}

function reportEviction(key: string, bytes: number): void {
  // Dev builds only: `development` (not `!== 'production'`) so the unit runner,
  // where NODE_ENV is unset, stays quiet.
  if (process.env.NODE_ENV !== 'development') return
  console.debug('[preview-budget] evicted', {
    key,
    bytes,
    ...getPreviewMemoryTelemetry(),
  })
}

/** Subscribe to heavy-viewer transitions. Returns the unsubscribe function. */
export function subscribePreviewMemory(listener: () => void): () => void {
  heavyViewerListeners.add(listener)
  return () => {
    heavyViewerListeners.delete(listener)
  }
}

/**
 * Subscribe to source-budget edges (over ↔ under). Deliberately separate from
 * the heavy-viewer channel: text previews must not re-render every time a
 * thumbnail wins or loses a viewer slot.
 */
export function subscribePreviewSourceBudget(listener: () => void): () => void {
  sourceBudgetListeners.add(listener)
  return () => {
    sourceBudgetListeners.delete(listener)
  }
}

export function getPreviewSourceBudgetVersion(): number {
  return sourceBudgetVersion
}

/** Monotonically increasing version, bumped on every heavy-viewer transition. */
export function getPreviewMemorySnapshotVersion(): number {
  return snapshotVersion
}

export type HeavyViewerSlotState = 'pending' | 'granted' | 'denied'

/**
 * One component's claim on a heavy-viewer slot.
 *
 * The registry — not the component — owns `state`. A component creates its
 * consumer, mounts it, and reads the state back through `useSyncExternalStore`;
 * it never derives the grant itself. That is what lets the registry re-decide
 * who holds a slot without any component polling or re-attempting.
 */
export interface HeavyViewerConsumer {
  state: HeavyViewerSlotState
  required: boolean
  /** Mount order. Last-resort tiebreak for consumers never seen on screen. */
  mountSeq: number
  /** Live: is this consumer currently near the scroll container's viewport. */
  visible: boolean
  /** When it last became visible. Higher = seen more recently. 0 = never. */
  visibleSeq: number
  /**
   * Has the visibility signal reported at least once.
   *
   * An optional consumer is not admitted before this flips, which is what
   * stops first paint from handing the slots out on mount order and then
   * reversing itself a frame later — churn that costs real bytes, because the
   * docx and spreadsheet viewers have no AbortController and their abandoned
   * fetches still come off the wire. `IntersectionObserver` always delivers an
   * initial callback per observed target, and the no-observer fallback reports
   * synchronously, so this cannot strand a viewer on its placeholder.
   */
  visibilityReported: boolean
}

const heavyViewerConsumers = new Set<HeavyViewerConsumer>()
let mountSeqCounter = 0
let visibleSeqCounter = 0

/** Create an unmounted consumer. Pure — safe to call during render. */
export function createHeavyViewerConsumer(): HeavyViewerConsumer {
  return {
    state: 'pending',
    required: false,
    mountSeq: 0,
    visible: false,
    visibleSeq: 0,
    visibilityReported: false,
  }
}

/**
 * Report whether a consumer is currently on screen, and re-run admission.
 *
 * This is a LIVE signal, not a one-way latch, and the difference is the whole
 * point. A latch is right for a fetch gate (#4033's `useInView`): once you
 * have decided to load something, scrolling away should not cancel it. It is
 * wrong for ranking a scarce resource — with a latch, every viewer eventually
 * reports "seen", every observer disconnects, and the ranking freezes at the
 * order things were *first* seen. Scroll down a long chat and back up and the
 * tiles in front of you would stay placeholders forever, because the rows
 * below already latched with a higher sequence and nothing could ever revise
 * it.
 *
 * `visibleSeq` advances only on a genuine off→on transition, so a viewer that
 * simply stays on screen never re-ranks itself and two viewers cannot trade a
 * slot back and forth. Changing rank requires the user to actually scroll.
 */
export function setHeavyViewerConsumerVisible(
  consumer: HeavyViewerConsumer,
  visible: boolean,
): void {
  const firstReport = !consumer.visibilityReported
  consumer.visibilityReported = true
  if (consumer.visible === visible && !firstReport) return
  consumer.visible = visible
  if (visible) {
    visibleSeqCounter += 1
    consumer.visibleSeq = visibleSeqCounter
  }
  if (!heavyViewerConsumers.has(consumer)) return
  scheduleAdmission()
}

/**
 * Re-evaluate every mounted consumer against the cap.
 *
 * Admission is **by visibility**, and a granted consumer CAN be demoted.
 *
 * Neither ordering rule that ignores the viewport works, because the two
 * surfaces disagree about where the user is looking. Mount order is the same
 * on both — the chat timeline is not virtualized yet, and the artifacts panel
 * / settings gallery each mount their whole list in one commit, all in
 * document order — but the user is at the BOTTOM of a chat and at the TOP of a
 * freshly opened list. So first-come starves the chat (all four slots pinned
 * by the oldest artifacts in the conversation, so the deliverable the current
 * turn just produced is a permanent static tile) and newest-wins starves the
 * gallery (the tiles above the fold lose to the ones below it). Both were
 * measured; each fixes one surface by breaking the other.
 *
 * Visibility is the signal both surfaces actually share, so that is what
 * ranks them. It also makes the rule survive PR-5's virtualization unchanged.
 *
 * Rules, in order:
 * - `required` consumers (full-mode, user-initiated) are always granted and
 *   consume capacity, so an open preview dialog costs a thumbnail its slot.
 *   They are never demoted — the user is looking straight at them.
 * - Optional consumers fill the remaining capacity: on-screen before
 *   off-screen, then most-recently-seen first, and mount order only as a
 *   last resort among consumers never seen at all.
 * - Demotion only ever hits thumbnails, which hold no user-editable state
 *   (page 1 of a pdf, a sheet summary), so being demoted and later re-promoted
 *   costs a refetch and nothing else.
 */
function admitHeavyViewerConsumers(): void {
  const optional: HeavyViewerConsumer[] = []
  let requiredCount = 0
  for (const consumer of heavyViewerConsumers) {
    if (consumer.required) requiredCount += 1
    else optional.push(consumer)
  }

  const capacity = Math.max(0, MAX_AUTO_MOUNTED_HEAVY_VIEWERS - requiredCount)
  const hasUnreportedOptional = optional.some(
    consumer => !consumer.visibilityReported,
  )
  optional.sort((a, b) => {
    if (a.visible !== b.visible) return a.visible ? -1 : 1
    if (a.visibleSeq !== b.visibleSeq) return b.visibleSeq - a.visibleSeq
    return b.mountSeq - a.mountSeq
  })

  let changed = false
  for (const consumer of heavyViewerConsumers) {
    if (!consumer.required) continue
    if (consumer.state !== 'granted') {
      consumer.state = 'granted'
      changed = true
    }
  }
  let taken = 0
  for (const consumer of optional) {
    // Not yet heard from the visibility signal: leave it pending rather than
    // guessing. Guessing is what produced the mount-order grant that first
    // paint then had to reverse.
    if (!consumer.visibilityReported) {
      if (consumer.state !== 'pending') {
        consumer.state = 'pending'
        changed = true
      }
      continue
    }
    // An off-screen consumer is only a fallback candidate. Until every
    // optional consumer has produced its initial visibility report, a pending
    // above-the-fold tile may outrank it. Granting the off-screen candidate
    // now would mount work that the settled first-paint ranking tears down.
    if (!consumer.visible && hasUnreportedOptional) {
      if (consumer.state !== 'pending') {
        consumer.state = 'pending'
        changed = true
      }
      continue
    }
    const next: HeavyViewerSlotState = taken < capacity ? 'granted' : 'denied'
    if (next === 'granted') taken += 1
    if (consumer.state === next) continue
    consumer.state = next
    changed = true
  }

  if (changed) notify()
}

let admissionScheduled = false

/**
 * Coalesce admission to once per task.
 *
 * Each viewer owns its own `IntersectionObserver`, so a page's initial reports
 * arrive as N separate callbacks within one "notify intersection observers"
 * step, in observe order — document order, which is not rank order. Admitting
 * on each report in turn therefore walks the registry through a sequence of
 * intermediate rankings before reaching the right one.
 *
 * Being precise about what that does and does not cost, because an earlier
 * version of this comment overstated it: those intermediate states do NOT
 * mount and tear down heavy bodies. The whole batch runs inside one task and
 * `useSyncExternalStore` flushes at the microtask checkpoint after it, so
 * React renders once, at the end — producing real mount churn would require a
 * render between individual callbacks, which the browser does not do. The
 * claim that this cost four wasted document loads was measured with a harness
 * that flushed React per callback, which is not reachable in a browser.
 *
 * What coalescing actually buys: one admission pass per task instead of N
 * (eight redundant sorts and eight redundant `notify` calls on a modest
 * gallery), and a registry that is never observed mid-batch in a ranking it is
 * about to abandon — which matters because `getPreviewMemoryTelemetry` and
 * every synchronous reader would otherwise see those transient states.
 * Reports that genuinely arrive in a later frame (a row mounted later, an
 * actual scroll) still get their own pass, which is correct.
 */
function scheduleAdmission(): void {
  if (admissionScheduled) return
  admissionScheduled = true
  queueMicrotask(() => {
    admissionScheduled = false
    admitHeavyViewerConsumers()
  })
}

/** Register a consumer as mounted and (re-)run admission. */
export function mountHeavyViewerConsumer(
  consumer: HeavyViewerConsumer,
  required: boolean,
): void {
  consumer.required = required
  mountSeqCounter += 1
  consumer.mountSeq = mountSeqCounter
  heavyViewerConsumers.add(consumer)
  scheduleAdmission()
}

/**
 * Unregister a consumer and re-run admission, which promotes whichever denied
 * thumbnail mounted most recently. Idempotent.
 */
export function unmountHeavyViewerConsumer(
  consumer: HeavyViewerConsumer,
): void {
  if (!heavyViewerConsumers.delete(consumer)) return
  consumer.state = 'pending'
  scheduleAdmission()
  // Admission may not change anything (nobody was waiting) and runs a
  // microtask later regardless, but the count changed now — keep subscribers
  // in sync immediately.
  notify()
}

export function readHeavyViewerConsumerState(
  consumer: HeavyViewerConsumer,
): HeavyViewerSlotState {
  return consumer.state
}

/** `useSyncExternalStore` server snapshot: nothing is ever mounted on the server. */
function readPendingHeavyViewerState(): HeavyViewerSlotState {
  return 'pending'
}

// ---------------------------------------------------------------------------
// Visibility detection
//
// The one-shot fetch gate and this continuous ranking signal deliberately use
// different observer lifecycles, but share the scroll root and lead distance.
// ---------------------------------------------------------------------------

/**
 * Report `element`'s on-screen state to `onChange` for as long as the returned
 * disconnect function is not called. Unlike #4033's fetch gate this does NOT
 * stop at the first intersection — see {@link setHeavyViewerConsumerVisible}
 * for why ranking needs the live signal.
 *
 * Eager fallback where `IntersectionObserver` does not exist (SSR of a client
 * component, the `tsx --test` / jsdom runners, very old browsers): the element
 * could never be reported visible, and treating everything as invisible would
 * rank every viewer last and strand the surface on placeholders. Those
 * environments report visible once, which degrades admission to the
 * pre-visibility ordering rather than breaking it.
 */
export function observePreviewElementVisibility(
  element: Element,
  onChange: (visible: boolean) => void,
): () => void {
  if (typeof IntersectionObserver === 'undefined') {
    onChange(true)
    return () => {}
  }
  const observer = new IntersectionObserver(
    entries => {
      const latest = entries[entries.length - 1]
      if (latest) onChange(latest.isIntersecting)
    },
    {
      root: getScrollParent(element),
      rootMargin: `${IN_VIEW_ROOT_MARGIN_PX}px`,
    },
  )
  observer.observe(element)
  return () => observer.disconnect()
}

/**
 * Callback-ref binding that keeps `consumer.visible` in sync with the attached
 * node. Shaped as a callback ref (not a RefObject) for the same reason #4033
 * gives: a later re-attach simply re-wires, instead of leaving a stale null
 * `current` with no observer and a tile stuck forever.
 *
 * Callers attach this to an element that is rendered in EVERY slot state. An
 * observer that only lives on the placeholder would disconnect the moment the
 * viewer is granted, and the registry would stop learning about that viewer's
 * position for good.
 */
export function createVisibilityBinding(consumer: HeavyViewerConsumer): {
  attach: (node: Element | null) => void
  disconnect: () => void
} {
  let cleanup: (() => void) | null = null

  const disconnect = () => {
    cleanup?.()
    cleanup = null
  }

  return {
    attach(node) {
      disconnect()
      if (!node) return
      cleanup = observePreviewElementVisibility(node, visible => {
        setHeavyViewerConsumerVisible(consumer, visible)
      })
    },
    disconnect,
  }
}

/**
 * Claim a heavy-viewer slot for the lifetime of the calling component.
 *
 * Returns the slot `state` plus a `ref` the caller MUST attach to an element
 * rendered in EVERY state — granted included. That ref is how the registry
 * tracks where the viewer is on screen, and visibility is what ranks competing
 * viewers. Two ways to get it wrong: dropping the ref ranks the viewer last
 * among contenders forever, and attaching it only to the placeholder makes the
 * observer disconnect the instant the viewer is granted, freezing that
 * viewer's position at whatever it was and blinding the registry to later
 * scrolling. `required` callers may omit it — they are admitted
 * unconditionally and never compete.
 *
 * `state` is `'pending'` on the first render (the claim happens in an effect,
 * so a concurrent render can never mutate the registry), then `'granted'` or
 * `'denied'`, and it can flip back to `'denied'` later if a viewer the user is
 * looking at needs the slot. Callers MUST NOT render (or fetch, or parse) the
 * heavy body unless the state is `'granted'` — degrading only the visible
 * output would save the DOM but not the bytes. Unmounting the heavy subtree on
 * demotion is what actually releases the parsed document.
 *
 * First paint: an optional viewer stays `'pending'` until the visibility
 * signal has reported at least once, so slots are never handed out on mount
 * order and then walked back. Every observed target gets an initial
 * `IntersectionObserver` callback, and the no-observer fallback reports
 * synchronously, so `'pending'` is always transient.
 */
export function useHeavyViewerSlot(required: boolean): {
  state: HeavyViewerSlotState
  ref: (node: Element | null) => void
} {
  // Lazy `useState` initializer, not a ref written during render: the object is
  // created once per component instance and never mutates the registry.
  const [consumer] = useState(createHeavyViewerConsumer)

  const getSnapshot = useCallback(
    () => readHeavyViewerConsumerState(consumer),
    [consumer],
  )
  const state = useSyncExternalStore(
    subscribePreviewMemory,
    getSnapshot,
    readPendingHeavyViewerState,
  )

  const binding = useMemo(() => createVisibilityBinding(consumer), [consumer])
  useEffect(() => binding.disconnect, [binding])

  // The registry owns the grant, so mounting is a pure external-system sync —
  // no setState in an effect, and no cascading render.
  // mount/unmount are exact inverses, so StrictMode's double-invoked effect
  // settles in the same state as a single invocation.
  useEffect(() => {
    mountHeavyViewerConsumer(consumer, required)
    return () => {
      unmountHeavyViewerConsumer(consumer)
    }
  }, [consumer, required])

  return { state, ref: binding.attach }
}

export function resetPreviewMemoryBudgetForTest(): void {
  sources.clear()
  measurements.clear()
  heavyViewerConsumers.clear()
  heavyViewerListeners.clear()
  sourceBudgetListeners.clear()
  snapshotVersion = 0
  sourceBudgetVersion = 0
  accessSeq = 0
  mountSeqCounter = 0
  visibleSeqCounter = 0
  admissionScheduled = false
  // Edge-triggered state: leaving these set makes the next test's first sync
  // look like a transition, so tests become order-dependent.
  refusalOutstanding = false
  lastAdmissionLoad = 0
}

/** The budget, for tests. Production code must go through `withinBudget`. */
export function previewSourceBudgetBytesForTest(): number {
  return PREVIEW_SOURCE_BUDGET_BYTES
}

/** Hard cap on tracked measurements. Test/diagnostics only. */
export const MAX_TRACKED_MEASUREMENTS_FOR_TEST = MAX_TRACKED_MEASUREMENTS

/** Number of measurements currently tracked. Test/diagnostics only. */
export function countPreviewMeasurementsForTest(): number {
  return measurements.size
}

/**
 * Run any pending admission pass immediately. Test-only.
 *
 * Production defers admission to a microtask so a whole batch of intersection
 * reports is considered together; tests use this to reach the settled state
 * without threading `await` through every assertion.
 */
export function flushHeavyViewerAdmissionForTest(): void {
  admissionScheduled = false
  admitHeavyViewerConsumers()
}

/** Read-only view of one accounted entry. Test/diagnostics only. */
export function inspectPreviewSourceForTest(key: string):
  | {
      bytes: number
      refCount: number
      lastAccessSeq: number
      evictOnLastRelease: boolean
    }
  | undefined {
  const entry = sources.get(key)
  if (!entry) return undefined
  return {
    bytes: entry.bytes,
    refCount: entry.refCount,
    lastAccessSeq: entry.lastAccessSeq,
    evictOnLastRelease: entry.evictOnLastRelease,
  }
}

/** Measured (not necessarily charged) wire size for a key. Test only. */
export function inspectPreviewMeasurementForTest(
  key: string,
): number | undefined {
  return measurements.get(key)
}
